import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, Plus, CreditCard, Upload, RefreshCw, Link2, Unlink,
  ArrowUpRight, ArrowDownLeft, CheckCircle2, XCircle, Search,
  FileText, Euro, Loader2, AlertTriangle, TrendingUp, TrendingDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BanqueSection({ buildingId, token }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showConnectBank, setShowConnectBank] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [filterReconcilie, setFilterReconcilie] = useState(null);

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/banque/comptes?building_id=${buildingId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAccounts(data);
        if (data.length > 0 && !selectedAccount) {
          setSelectedAccount(data[0]);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [buildingId, token, selectedAccount]);

  const fetchTransactions = useCallback(async () => {
    if (!selectedAccount) return;
    
    try {
      let url = `${API_URL}/api/banque/transactions?account_id=${selectedAccount.id}`;
      if (filterReconcilie !== null) {
        url += `&reconcilie=${filterReconcilie}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setTransactions(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }, [selectedAccount, token, filterReconcilie]);

  const fetchStats = useCallback(async () => {
    if (!selectedAccount) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/banque/transactions/stats?account_id=${selectedAccount.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }, [selectedAccount, token]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    if (selectedAccount) {
      fetchTransactions();
      fetchStats();
    }
  }, [selectedAccount, fetchTransactions, fetchStats]);

  const handleSync = async () => {
    if (!selectedAccount?.open_banking_connected) {
      toast.error('Compte non connecté à Open Banking');
      return;
    }
    
    setSyncing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/banque/openbanking/sync/${selectedAccount.id}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchTransactions();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur de synchronisation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSyncing(false);
    }
  };

  const handleAutoReconcile = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/banque/reconcilier/auto?account_id=${selectedAccount.id}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchTransactions();
      } else {
        toast.error('Erreur lors du rapprochement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Comptes Bancaires</h2>
          <p className="text-sm text-gray-500">
            Gestion des comptes de la copropriété et rapprochement bancaire
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddAccount(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892]"
            data-testid="add-account-btn"
          >
            <Plus className="h-5 w-5" />
            Ajouter un compte
          </button>
        </div>
      </div>

      {/* Account Selector */}
      {accounts.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {accounts.map(account => (
            <button
              key={account.id}
              onClick={() => setSelectedAccount(account)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all ${
                selectedAccount?.id === account.id
                  ? 'border-[#00D3A8] bg-[#00D3A8]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`account-${account.id}`}
            >
              <CreditCard className={`h-5 w-5 ${selectedAccount?.id === account.id ? 'text-[#00D3A8]' : 'text-gray-400'}`} />
              <div className="text-left">
                <p className="font-medium text-gray-900">{account.nom}</p>
                <p className="text-xs text-gray-500">{account.banque}</p>
              </div>
              {account.open_banking_connected && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                  Connecté
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* No accounts */}
      {accounts.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <CreditCard className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun compte bancaire</h3>
          <p className="text-gray-500 mb-4">Ajoutez le compte bancaire dédié à cette copropriété</p>
          <button
            onClick={() => setShowAddAccount(true)}
            className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892]"
          >
            Ajouter un compte
          </button>
        </div>
      )}

      {/* Account Details */}
      {selectedAccount && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Solde actuel</p>
                  <p className={`text-2xl font-bold mt-1 ${(stats?.solde_actuel || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatMoney(stats?.solde_actuel)}
                  </p>
                </div>
                <Euro className="h-8 w-8 text-gray-200" />
              </div>
              {stats?.solde_date && (
                <p className="text-xs text-gray-400 mt-2">Au {formatDate(stats.solde_date)}</p>
              )}
            </div>
            
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Encaissements (30j)</p>
                  <p className="text-2xl font-bold mt-1 text-green-600">
                    {formatMoney(stats?.credits_30j)}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-200" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Décaissements (30j)</p>
                  <p className="text-2xl font-bold mt-1 text-red-600">
                    {formatMoney(stats?.debits_30j)}
                  </p>
                </div>
                <TrendingDown className="h-8 w-8 text-red-200" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">À rapprocher</p>
                  <p className="text-2xl font-bold mt-1 text-orange-600">
                    {stats?.transactions_non_reconciliees || 0}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-200" />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowImport(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              data-testid="import-btn"
            >
              <Upload className="h-5 w-5 text-gray-600" />
              Importer relevé
            </button>
            
            {selectedAccount.open_banking_connected ? (
              <>
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                  data-testid="sync-btn"
                >
                  {syncing ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-5 w-5" />
                  )}
                  Synchroniser
                </button>
                <button
                  onClick={() => {/* disconnect */}}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                >
                  <Unlink className="h-5 w-5" />
                  Déconnecter
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowConnectBank(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                data-testid="connect-bank-btn"
              >
                <Link2 className="h-5 w-5" />
                Connecter la banque
              </button>
            )}
            
            {stats?.transactions_non_reconciliees > 0 && (
              <button
                onClick={handleAutoReconcile}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                data-testid="auto-reconcile-btn"
              >
                <CheckCircle2 className="h-5 w-5" />
                Rapprochement auto
              </button>
            )}
          </div>

          {/* Transactions Filter */}
          <div className="flex items-center gap-4 bg-white rounded-xl border border-gray-200 p-4">
            <span className="text-sm font-medium text-gray-700">Filtrer :</span>
            <button
              onClick={() => setFilterReconcilie(null)}
              className={`px-3 py-1 rounded-full text-sm ${filterReconcilie === null ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              Toutes
            </button>
            <button
              onClick={() => setFilterReconcilie(false)}
              className={`px-3 py-1 rounded-full text-sm ${filterReconcilie === false ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              À rapprocher
            </button>
            <button
              onClick={() => setFilterReconcilie(true)}
              className={`px-3 py-1 rounded-full text-sm ${filterReconcilie === true ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              Rapprochées
            </button>
          </div>

          {/* Transactions List */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Libellé</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map(trans => (
                  <tr key={trans.id} className={`hover:bg-gray-50 ${!trans.reconcilie ? 'bg-orange-50/30' : ''}`}>
                    <td className="px-4 py-3 text-sm text-gray-900">{formatDate(trans.date_operation)}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900 truncate max-w-md">{trans.libelle || '-'}</p>
                      {trans.reference && (
                        <p className="text-xs text-gray-500">Réf: {trans.reference}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-medium ${trans.montant >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {trans.montant >= 0 ? '+' : ''}{formatMoney(trans.montant)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {trans.reconcilie ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                          <CheckCircle2 className="h-3 w-3" />
                          Rapproché
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                          <XCircle className="h-3 w-3" />
                          À rapprocher
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-1 rounded ${
                        trans.source === 'open_banking' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {trans.source === 'open_banking' ? 'Open Banking' : 'Import'}
                      </span>
                    </td>
                  </tr>
                ))}
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                      Aucune transaction
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Add Account Modal */}
      {showAddAccount && (
        <AddAccountModal
          buildingId={buildingId}
          token={token}
          onClose={() => setShowAddAccount(false)}
          onCreated={() => {
            setShowAddAccount(false);
            fetchAccounts();
          }}
        />
      )}

      {/* Import Modal */}
      {showImport && selectedAccount && (
        <ImportModal
          accountId={selectedAccount.id}
          token={token}
          onClose={() => setShowImport(false)}
          onImported={() => {
            setShowImport(false);
            fetchTransactions();
            fetchStats();
          }}
        />
      )}

      {/* Connect Bank Modal */}
      {showConnectBank && selectedAccount && (
        <ConnectBankModal
          accountId={selectedAccount.id}
          token={token}
          onClose={() => setShowConnectBank(false)}
          onConnected={() => {
            setShowConnectBank(false);
            fetchAccounts();
          }}
        />
      )}
    </div>
  );
}

// Add Account Modal
function AddAccountModal({ buildingId, token, onClose, onCreated }) {
  const [form, setForm] = useState({
    nom: 'Compte principal',
    banque: '',
    iban: '',
    bic: '',
    type_compte: 'courant'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/banque/comptes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...form, building_id: buildingId })
      });

      if (response.ok) {
        toast.success('Compte créé avec succès');
        onCreated();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">Nouveau compte bancaire</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom du compte</label>
            <input
              type="text"
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Banque</label>
            <input
              type="text"
              value={form.banque}
              onChange={(e) => setForm({ ...form, banque: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              placeholder="Ex: Crédit Mutuel"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">IBAN</label>
            <input
              type="text"
              value={form.iban}
              onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg font-mono"
              placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">BIC</label>
            <input
              type="text"
              value={form.bic}
              onChange={(e) => setForm({ ...form, bic: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg font-mono"
              placeholder="CMCIFRPP"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type de compte</label>
            <select
              value={form.type_compte}
              onChange={(e) => setForm({ ...form, type_compte: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg"
            >
              <option value="courant">Compte courant</option>
              <option value="epargne">Compte épargne</option>
              <option value="travaux">Compte travaux</option>
            </select>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892] disabled:opacity-50"
            >
              {saving ? 'Création...' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Import Modal
function ImportModal({ accountId, token, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleImport = async () => {
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(
        `${API_URL}/api/banque/import?account_id=${accountId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.imported} transaction(s) importée(s)`);
        if (result.duplicates > 0) {
          toast.info(`${result.duplicates} doublon(s) ignoré(s)`);
        }
        onImported();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'import');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">Importer un relevé bancaire</h3>
        
        <div className="space-y-4">
          <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
            <Upload className="h-10 w-10 mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-600 mb-2">
              Formats supportés : MT940, CFONB, CSV
            </p>
            <input
              type="file"
              accept=".mt940,.sta,.txt,.csv"
              onChange={(e) => setFile(e.target.files[0])}
              className="hidden"
              id="file-input"
            />
            <label
              htmlFor="file-input"
              className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200"
            >
              Choisir un fichier
            </label>
            {file && (
              <p className="mt-3 text-sm text-[#00D3A8] font-medium">{file.name}</p>
            )}
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>Astuce :</strong> Téléchargez votre relevé depuis l'espace en ligne de votre banque. 
              La plupart des banques proposent l'export au format MT940 ou CSV.
            </p>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Annuler
            </button>
            <button
              onClick={handleImport}
              disabled={!file || importing}
              className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892] disabled:opacity-50"
            >
              {importing ? 'Import en cours...' : 'Importer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Connect Bank Modal (Open Banking)
function ConnectBankModal({ accountId, token, onClose, onConnected }) {
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    const fetchInstitutions = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/banque/openbanking/institutions?country=FR`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setInstitutions(data.institutions || []);
          if (data.message && data.institutions?.length === 0) {
            toast.info(data.message);
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInstitutions();
  }, [token]);

  const handleConnect = async (institutionId) => {
    setConnecting(true);
    try {
      const response = await fetch(`${API_URL}/api/banque/openbanking/connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_id: accountId,
          institution_id: institutionId,
          redirect_url: `${window.location.origin}/comptabilite?tab=banque&callback=true`
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.link) {
          window.location.href = data.link;
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur de connexion');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setConnecting(false);
    }
  };

  const filteredInstitutions = institutions.filter(inst =>
    inst.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <h3 className="text-lg font-semibold mb-4">Connecter votre banque</h3>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#00D3A8]" />
          </div>
        ) : institutions.length === 0 ? (
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 mx-auto text-orange-400 mb-4" />
            <p className="text-gray-600 mb-2">Open Banking non configuré</p>
            <p className="text-sm text-gray-500">
              Pour activer cette fonctionnalité, ajoutez vos clés GoCardless dans la configuration.
            </p>
            <div className="mt-4 p-3 bg-gray-50 rounded-lg text-left text-xs text-gray-600 font-mono">
              GOCARDLESS_SECRET_ID=xxx<br />
              GOCARDLESS_SECRET_KEY=xxx
            </div>
          </div>
        ) : (
          <>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher une banque..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg"
              />
            </div>
            
            <div className="flex-1 overflow-y-auto space-y-2">
              {filteredInstitutions.map(inst => (
                <button
                  key={inst.id}
                  onClick={() => handleConnect(inst.id)}
                  disabled={connecting}
                  className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-[#00D3A8] hover:bg-[#00D3A8]/5 transition-colors disabled:opacity-50"
                >
                  {inst.logo ? (
                    <img src={inst.logo} alt="" className="h-8 w-8 object-contain" />
                  ) : (
                    <Building2 className="h-8 w-8 text-gray-400" />
                  )}
                  <span className="font-medium text-gray-900">{inst.name}</span>
                </button>
              ))}
            </div>
          </>
        )}
        
        <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
